import React from 'react';
import type { TopAdData } from '../../hooks/use-social-ads-overview-tab';
import { ADS_ROW_TYPE, type AdsRowType } from '../../social-ads-top-ads';

// Extract formatters into a clean object structure
const formatters = {
  currency: (value: number) =>
    `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
  number: (value: number) => value.toLocaleString(),
  percentage: (value: number, decimals = 2) => `${value.toFixed(decimals)}%`,
  cpm: (value: number) => `$${value.toFixed(2)}`,
  cpc: (value: number) => `$${value.toFixed(3)}`,
  roas: (value: number | undefined) => (value !== undefined && value !== null ? `${value.toFixed(2)}X` : ''),
};

// Legacy function for backward compatibility
export function sharedRenderRow(rowType: AdsRowType, row: { original: TopAdData }, col: any) {
  // Helper function to safely get name from union type
  const getAdName = (by: TopAdData['by']) => {
    if (by?.__typename === 'MetaAd' && 'name' in by) {
      return by.name;
    }
    if (by?.__typename === 'MetaAdAccount' && 'name' in by) {
      return by.name;
    }
    if (by?.__typename === 'MetaAdCampaign' && 'name' in by) {
      return by.name;
    }
    if (by?.__typename === 'MetaAdSet' && 'name' in by) {
      return by.name;
    }
    return '';
  };

  // Helper function to get comma-separated usernames
  const getUsernames = (by: TopAdData['by']): string => {
    if (by?.__typename === 'MetaAd' && 'metaAdMetaAdCreatives' in by) {
      const usernames =
        by.metaAdMetaAdCreatives?.map((creative) => creative?.socialAccount?.username).filter(Boolean) || [];
      return usernames.join(', ');
    }
    return '';
  };

  const accessorKey = col.id;

  switch (rowType) {
    case ADS_ROW_TYPE.AD_NAME: {
      const adName = getAdName(row.original.by);
      return (
        <span
          className="font-medium text-gray-900 max-w-[180px] truncate inline-block align-middle"
          tabIndex={0}
          aria-label={`Ad name: ${adName}`}
          title={adName}
        >
          {adName}
        </span>
      );
    }
    case ADS_ROW_TYPE.SOCIAL_ACCOUNT: {
      const usernames = getUsernames(row.original.by);
      return (
        <span className="text-gray-600" aria-label={`Social account usernames: ${usernames}`}>
          {usernames}
        </span>
      );
    }
    case ADS_ROW_TYPE.ROAS: {
      const value = row.original.metrics?.roas;
      const formattedValue = formatters.roas(value);
      return (
        <span className="font-medium text-gray-900" aria-label={`ROAS: ${formattedValue || 'Not available'}`}>
          {formattedValue}
        </span>
      );
    }
    case ADS_ROW_TYPE.CURRENCY: {
      let value = 0;
      switch (accessorKey) {
        case 'metrics.spend.amount':
          value = row.original.metrics?.spend?.amount ?? 0;
          break;
        case 'metrics.purchase.amount':
          value = row.original.metrics?.purchase?.amount ?? 0;
          break;
        case 'metrics.cpm':
          value = row.original.metrics?.cpm ?? 0;
          break;
        case 'metrics.cpc':
          value = row.original.metrics?.cpc ?? 0;
          break;
      }
      const formattedValue =
        accessorKey === 'metrics.cpm' || accessorKey === 'metrics.cpc'
          ? accessorKey === 'metrics.cpm'
            ? formatters.cpm(value)
            : formatters.cpc(value)
          : formatters.currency(value);
      return (
        <span className="text-gray-600" aria-label={`${col.header}: ${formattedValue}`}>
          {formattedValue}
        </span>
      );
    }
    case ADS_ROW_TYPE.PERCENTAGE: {
      const value = row.original.metrics?.ctr ?? 0;
      const formattedValue = formatters.percentage(value);
      return (
        <span className="text-gray-600" aria-label={`${col.header}: ${formattedValue}`}>
          {formattedValue}
        </span>
      );
    }
    case ADS_ROW_TYPE.NUMBER: {
      let value = 0;
      switch (accessorKey) {
        case 'metrics.impressions':
          value = row.original.metrics?.impressions ?? 0;
          break;
        case 'metrics.reach':
          value = row.original.metrics?.reach ?? 0;
          break;
        case 'metrics.clicks':
          value = row.original.metrics?.clicks ?? 0;
          break;
      }
      const formattedValue = formatters.number(value);
      return (
        <span className="text-gray-600" aria-label={`${col.header}: ${formattedValue}`}>
          {formattedValue}
        </span>
      );
    }
    default: {
      const genericValue = row.original[accessorKey as keyof TopAdData];
      const genericStringValue = genericValue != null ? String(genericValue) : '';
      return (
        <span className="text-gray-600" aria-label={`${col.header}: ${genericStringValue || 'Not available'}`}>
          {genericStringValue}
        </span>
      );
    }
  }
}
